import { Box, Button, CircularProgress, InputAdornment, TextField, Typography, Checkbox } from "@mui/material";
import { grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { debounce } from "lodash";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { DownloadRounded } from "@mui/icons-material";
import {
	shootMerchantSampleDetailsColumns,
	shootSkuActionDgCol,
	shootSkuIcodeColumn,
	shootSkuItemMasterColumns,
	shootStudioSampleDetailsColumns,
	merchantUpdatedByColumn,
	studioUpdatedByColumn,
} from "components/PimDashboard/DashboardComponents/Studio/ShootsDashboard/data";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { publishTableColumn } from "components/PimDashboard/data";

const SkuSectionTanstack = ({
	shootData,
	selectedSkus, // array of strings (i_code)
	setSelectedSkus, // setter for array of strings
	minHeight = 300,
	maxHeight = 300,
	onRowClick = (row, event) => {},
	skusState, // optional
	setSkusState, // optional
	hideSearchHeader = false,
	showActionColumn = false,
	actionList = [],
	disableSelection = false,
	processCheckboxSelectionChange,
	childrenAboveTable,
	disablePagination = false,
	defaultFilterObj = {},
	enableExcelExport = false,
	exportFileName = "Combined_SKUs.xlsx",
}) => {
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();
	const [filterObj, setFilterObj] = useState({});
	const [tableColumns] = useState([
		shootSkuIcodeColumn,
		...Object.values(shootSkuItemMasterColumns),
		...Object.values(shootStudioSampleDetailsColumns),
		studioUpdatedByColumn,
		...Object.values(shootMerchantSampleDetailsColumns),
		merchantUpdatedByColumn,
	]);

	const getSkus = async (limit = 20, offset = 0, filterObj = {}) => {
		// Keep screen-level defaults on every server call while still allowing grid filters like search.
		const mergedFilterObj = { ...defaultFilterObj, ...filterObj };
		const queryObj = { limit, offset };
		if (mergedFilterObj.search) queryObj["search"] = mergedFilterObj.search;
		const res = await axios.post(`/api/studio/skus_combined_data`, { filter: mergedFilterObj }, { params: queryObj });
		return res.data.result;
	};

	const getMergedFilterObj = () => ({ ...defaultFilterObj, ...filterObj });

	const getDownloadFilename = (headers = {}) => {
		const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"];
		const fallbackFileName = exportFileName || "Combined_SKUs.xlsx";
		if (!disposition) return fallbackFileName;

		const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
		if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

		const fileNameMatch = disposition.match(/filename=\"?([^\"]+)\"?/i);
		return fileNameMatch?.[1] || fallbackFileName;
	};

	const handleExcelExport = async () => {
		const mergedFilterObj = getMergedFilterObj();
		const queryObj = { excel_export: true };
		if (mergedFilterObj.search) queryObj.search = mergedFilterObj.search;

		setLoadingOpen(true);
		try {
			const response = await axios.post(
				`/api/studio/skus_combined_data`,
				{ filter: mergedFilterObj },
				{
					params: queryObj,
					responseType: "blob",
				},
			);

			const blob = new Blob([response.data], {
				type: response.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const downloadUrl = window.URL.createObjectURL(blob);
			const anchor = document.createElement("a");
			anchor.href = downloadUrl;
			anchor.download = getDownloadFilename(response.headers);
			document.body.appendChild(anchor);
			anchor.click();
			document.body.removeChild(anchor);
			window.URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(error),
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	// Convert table columns to TanStack format
	const tanstackColumns = useMemo(() => {
		const allCols = [
			...(showActionColumn ? [shootSkuActionDgCol(actionList)] : []),
			publishTableColumn(() => {}, true),
			...tableColumns,
		];

		return allCols.map((col) => ({
			minWidth: 130,
			maxWidth: 200,
			...col,
			key: col.key || col.field,
			name:
				col.name?.toUpperCase() ||
				col.key?.toUpperCase() ||
				col.headerName?.toUpperCase() ||
				col.field?.toUpperCase() ||
				"COLUMN",
			headerAlign: "center",
			align: "center",
			flex: 1,
		}));
	}, [tableColumns, showActionColumn, actionList]);

	// Handle checkbox selection
	const handleCheckboxChange = (selectedIds) => {
		if (typeof processCheckboxSelectionChange === "function") {
			processCheckboxSelectionChange(selectedIds);
			return;
		}

		setSelectedSkus(selectedIds);
	};

	return (
		<Box display="flex" flexWrap="wrap" width="100%" rowGap={1}>
			<Box width="100%" height={maxHeight}>
				<TanstackDatagrid
					pageData={skusState}
					setPageData={setSkusState}
					filterObj={filterObj}
					setFilterObj={setFilterObj}
					columns={tanstackColumns}
					getRowId={(row) => row.i_code}
					checkboxSelection={!disableSelection}
					selectedRowIds={selectedSkus}
					setSelectedRowIds={setSelectedSkus}
					processCheckboxSelectionChange={handleCheckboxChange}
					handleRowClick={({ row }, e) => onRowClick(row, e)}
					disablePagination={disablePagination}
					rowsPerPage={20}
					serverSidePagination={true}
					getServerSideData={getSkus}
					emptyMessage="No SKUs found"
					headerHeight={35}
					rowHeight={40}
					enableHorizontalScroll={true}
					children={
						<Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
							{childrenAboveTable ?? <Box />}
							{enableExcelExport && (
								<Button variant="contained" size="small" onClick={handleExcelExport} startIcon={<DownloadRounded />}>
									Export Excel
								</Button>
							)}
						</Box>
					}
				/>
			</Box>
		</Box>
	);
};

export default SkuSectionTanstack;
